import isEqual from 'lodash-es/isEqual';
import store from '../../store';
import { BUTTONS_VISIBILITY, USER_TYPES } from '../../constants';

const DEFAULT_MODULE_FUNCTIONS = {
    has_upgrade_option: false,
    has_speech: false,
    has_listen_in_option: false,
    has_acoustic_option: false,
    enabled: false,
    upgrade: false,
    listen_in: false,
    acoustic: false,
    threshold: 0,
    duration: 100,
};

class Module {
    constructor(module) {
        // merge properties
        Object.assign(this, {
            ...module,
            functions: { ...DEFAULT_MODULE_FUNCTIONS, ...(module || {}).functions },
        });

        // StaffServer returns these properties as strings
        this.functions.threshold = Number(this.functions.threshold);
        this.functions.duration = Number(this.functions.duration);

        // isChanged method compares this initial image with current module config
        this.initFunctions = { ...this.functions };

        this.initButtonsVisibility = this.buttons_visibility;
    }

    isChanged() {
        return !isEqual(this.initFunctions, this.functions);
    }

    getButtonsVisibility() {
        return (
            this.buttons_visibility === BUTTONS_VISIBILITY.TO_ALL ||
            (this.buttons_visibility === BUTTONS_VISIBILITY.TO_SU &&
                [USER_TYPES.USER_ADMIN, USER_TYPES.SUPER_USER].includes(store.state.user.user.type))
        );
    }

    setButtonsVisibility(val) {
        this.buttons_visibility = val;
    }

    isButtonsVisibilityChanged() {
        return this.initButtonsVisibility !== this.buttons_visibility;
    }

    isButtonsVisibilityNotToAll() {
        return this.buttons_visibility !== BUTTONS_VISIBILITY.TO_ALL;
    }

    getButtonsVisibilityChanges() {
        return {
            peripheral_id: this.peripheral_id,
            name: this.name,
            buttons_visibility: this.buttons_visibility,
        };
    }

    getThreshold() {
        return this.functions.threshold;
    }

    setThreshold(val) {
        if (this.hasAcousticOption()) {
            this.functions.threshold = Number(val);
        }
    }

    getDuration() {
        return this.functions.duration;
    }

    setDuration(val) {
        if (this.hasAcousticOption()) {
            this.functions.duration = Number(val);
        }
    }

    hasListenInOption() {
        return this.getButtonsVisibility() && this.functions.has_listen_in_option;
    }

    getListenInStatus() {
        return this.functions.listen_in;
    }

    setListenInStatus(val) {
        if (this.hasListenInOption()) {
            this.functions.listen_in = !!val;
        }
    }

    toggleListenInStatus() {
        this.setListenInStatus(!this.getListenInStatus());
    }

    hasUpgradeOption() {
        return this.getButtonsVisibility() && this.functions.has_upgrade_option;
    }

    getUpgradeStatus() {
        return !!this.functions.upgrade;
    }

    setUpgradeStatus(val) {
        if (this.hasUpgradeOption()) {
            this.functions.upgrade = !!val;
        }
    }

    toggleUpgradeStatus() {
        this.setUpgradeStatus(!this.getUpgradeStatus());
    }

    hasAcousticOption() {
        return this.getButtonsVisibility() && this.functions.has_acoustic_option;
    }

    getAcousticStatus() {
        return !!this.functions.acoustic;
    }

    setAcousticStatus(val) {
        if (this.hasAcousticOption()) {
            this.functions.acoustic = !!val;
        }
    }

    toggleAcousticStatus() {
        this.setAcousticStatus(!this.getAcousticStatus());
    }

    hasSpeech() {
        return this.functions.has_speech;
    }

    getSpeechIcon() {
        if (this.hasSpeech()) {
            return this.hasAcousticOption()
                ? 'static/img/devices/NISP_Acoustic_24px.png'
                : 'static/img/devices/NISP_24px.png';
        }
    }

    getIcon() {
        if (this.is_nuspm && this.hasAcousticOption()) {
            return 'static/img/devices/' + this.icon_name + '_Acoustic_24px.png';
        }
        return 'static/img/devices/' + this.icon_name + '_24px.png';
    }

    hasEnabledOption() {
        return this.getButtonsVisibility() && typeof this.functions.enabled === 'boolean';
    }

    toggleEnabledStatus() {
        this.setEnabledStatus(!this.getEnabledStatus());
    }

    getEnabledStatus() {
        return !!this.functions.enabled;
    }

    setEnabledStatus(val) {
        if (this.hasEnabledOption()) {
            // Here if button is visible
            if (!this.is_numi && !this.has_nima) {
                // Here if module is not NIMA/NUMI and not connected to NIMA
                this.functions.enabled = !!val;
            } else {
                // Here if module is NIMA, NUMI, or connected to NIMA
                if (val) {
                    // Only allow to enable calls
                    this.functions.enabled = !!val;
                }
            }
        }
    }

    getSaveData() {
        return {
            peripheral_id: this.peripheral_id,
            name: this.name,
            functions: this.functions,
        };
    }
}

export default Module;
